// src/pages/admin/ver-taller.tsx
// REFACTORIZADO: Usa el hook useTaller para centralizar la lógica

import React, { useEffect, useMemo, useState } from 'react';
import Navbar from '../../components/navbar/navbar';
import UserNav from '../../components/navbar/user-nav';
import FooterTop from '../../components/footer-top';
import Footer from '../../components/footer';
import { Link, useNavigate } from 'react-router-dom';
import { useUser } from '../../context/UserContext';

// Importar el hook y tipos desde useTaller
import { useTaller, Taller, TallerFilters } from '../../hooks/useTaller';

// Importar helpers de estado
import { puedeRegistrarse, esTallerActivo, obtenerBadgeEstado } from '../../utils/tallerEstadoHelper';

// Importar componentes de loading y PageTitle
import { LoadingMessage, ErrorState, EmptyState } from '../../components/loading-states';
import PageTitle from '../../components/page-title';

export default function VerTalleres() {
  const { user } = useUser();
  const navigate = useNavigate();

  // ========== USAR EL HOOK useTaller ==========
  const {
    talleres,
    loading,
    error,
    fetchAllTalleres,
    filtrarTalleres,
    formatearCosto
  } = useTaller();

  // Estado de filtros
  const [filtros, setFiltros] = useState<TallerFilters>({
    nombre: '',
    mes: '',
    costo: 'todos',
    creditos: 'todos',
    estado: 'todos'
  });

  // Estado para mostrar/ocultar filtros (colapsable en móvil)
  const [mostrarFiltros, setMostrarFiltros] = useState(false);

  // Cargar talleres al montar el componente
  useEffect(() => {
    fetchAllTalleres(false); // false = no necesitamos conteo de inscritos aquí
  }, [fetchAllTalleres]);

  // Aplicar filtros
  const talleresFiltrados = useMemo(() => {
    return filtrarTalleres(talleres, filtros);
  }, [talleres, filtros, filtrarTalleres]);

  // Estado para mostrar/ocultar sección de finalizados
  const [mostrarFinalizados, setMostrarFinalizados] = useState(false);

  // Separar talleres usando estadoCalculado (incluye precisión con horas)
  const { activos, finalizados } = useMemo(() => {
    const act = talleresFiltrados
      .filter(t => esTallerActivo(t.estadoCalculado))
      .sort((a, b) => {
        const fechaA = (a.fecha?.toDate?.() || a.fechaInicio?.toDate?.())?.getTime() || 0;
        const fechaB = (b.fecha?.toDate?.() || b.fechaInicio?.toDate?.())?.getTime() || 0;
        return fechaA - fechaB;
      });

    const fin = talleresFiltrados
      .filter(t => t.estadoCalculado === 'finalizado' || t.estadoCalculado === 'cancelado')
      .sort((a, b) => {
        const fechaA = (a.fecha?.toDate?.() || a.fechaInicio?.toDate?.())?.getTime() || 0;
        const fechaB = (b.fecha?.toDate?.() || b.fechaInicio?.toDate?.())?.getTime() || 0;
        return fechaB - fechaA;
      });

    return { activos: act, finalizados: fin };
  }, [talleresFiltrados]);

  // Actualizar un filtro específico
  const actualizarFiltro = (campo: keyof TallerFilters, valor: string) => {
    setFiltros(prev => ({ ...prev, [campo]: valor }));
  };

  // Limpiar todos los filtros
  const limpiarFiltros = () => {
    setFiltros({
      nombre: '',
      mes: '',
      costo: 'todos',
      creditos: 'todos',
      estado: 'todos'
    });
  };

  // Verificar si hay filtros activos
  const hayFiltrosActivos = filtros.nombre || filtros.mes ||
    filtros.costo !== 'todos' || filtros.creditos !== 'todos' || filtros.estado !== 'todos';

  // Determinar si se puede inscribir a un taller usando la lógica con buffer de 30 min
  const tallerPermiteRegistro = (taller: Taller): boolean => {
    const fecha = taller.fecha?.toDate?.() || taller.fechaInicio?.toDate?.();
    if (!fecha) return false;
    return puedeRegistrarse(
      fecha,
      taller.horaInicio || '00:00',
      taller.horaTermino || '23:59',
      taller.estado
    );
  };

  // Renderizar una tarjeta de taller
  const renderTaller = (taller: Taller, compacto: boolean = false) => {
    const fecha = taller.fecha?.toDate?.() || taller.fechaInicio?.toDate?.();
    const badge = obtenerBadgeEstado(taller.estadoCalculado);
    const permiteRegistro = tallerPermiteRegistro(taller);

    return (
      <div key={taller.id} className={compacto ? 'col-md-6 col-lg-4 mb-3' : 'col-md-6 col-lg-4 mb-4'}>
        <div className={`card h-100 shadow-sm p-3 ${compacto ? 'border-0 bg-light' : ''}`}>
          {taller.bannerUrl && !compacto && (
            <img
              src={taller.bannerUrl}
              alt={`Banner de ${taller.nombre}`}
              className="img-fluid mb-3 rounded"
              style={{ maxHeight: '200px', objectFit: 'cover', width: '100%' }}
            />
          )}
          <div className="d-flex justify-content-between align-items-start mb-2">
            <h5 className="mb-0">{taller.nombre}</h5>
            <span className={`badge ${badge.className} ms-2`} style={{ whiteSpace: 'nowrap' }}>
              {badge.texto}
            </span>
          </div>
          <p className="mb-1">
            <strong>Fecha:</strong>{' '}
            {fecha?.toLocaleDateString('es-MX') || 'Por definir'}
          </p>
          <p className="mb-1">
            <strong>Horario:</strong>{' '}
            {taller.horaInicio && taller.horaTermino
              ? `${taller.horaInicio} - ${taller.horaTermino}`
              : taller.horario || 'Por definir'}
          </p>
          <p className="mb-1"><strong>Lugar:</strong> {taller.lugar}</p>
          {taller.ciudad && <p className="mb-1"><strong>Ciudad:</strong> {taller.ciudad}</p>}
          {!compacto && (
            <>
              <p className="mb-1">
                <strong>Organizador:</strong>{' '}
                {taller.organizadorPrincipal || taller.expositor || 'AMPI'}
              </p>
              {taller.tipoEvento && (
                <p className="mb-1">
                  <strong>Modalidad:</strong>{' '}
                  <span className="badge bg-info">{taller.tipoEvento}</span>
                </p>
              )}
            </>
          )}
          <p className="mb-1">
            <strong>Costo:</strong>{' '}
            {formatearCosto(taller)}
          </p>
          {!compacto && (
            <>
              <p className="mb-1">
                <strong>Cupo:</strong>{' '}
                {taller.cupoMaximo || taller.cupo || 'Ilimitado'}
              </p>
              <p className="mb-3"><strong>Créditos:</strong> {taller.creditos || 0}</p>
            </>
          )}

          <div className="d-flex justify-content-between mt-auto">
            {user?.tipo?.includes('admin') && (
              <button
                className="btn btn-sm btn-outline-dark"
                onClick={() => navigate(`/ver-taller-ind/${taller.id}`)}
              >
                Administrar
              </button>
            )}
            {permiteRegistro && (
              <Link to={`/ins-taller/${taller.id}`} className="btn btn-sm btn-primary ms-auto">
                Inscribirme
              </Link>
            )}
            {taller.estadoCalculado === 'en_curso' && !permiteRegistro && (
              <span className="badge bg-warning text-dark ms-auto align-self-center">
                Registro cerrado
              </span>
            )}
          </div>
        </div>
      </div>
    );
  };

  // ========== RENDER ==========

  // Estado de carga
  if (loading) {
    return (
      <>
        {user ? (
          <UserNav nombreUsuario={user.nombre} nombreInmobiliaria={user.inmobiliaria_nombre} />
        ) : (
          <Navbar transparent={false} />
        )}
        <PageTitle title="Listado de Talleres" subtitle="Explora los talleres disponibles" />
        <section className="gray-simple">
          <div className="container py-5">
            <LoadingMessage message="Cargando talleres..." subMessage="Por favor espera" />
          </div>
        </section>
        <FooterTop bg="theme-bg" />
        <Footer />
      </>
    );
  }

  // Estado de error
  if (error) {
    return (
      <>
        {user ? (
          <UserNav nombreUsuario={user.nombre} nombreInmobiliaria={user.inmobiliaria_nombre} />
        ) : (
          <Navbar transparent={false} />
        )}
        <PageTitle title="Listado de Talleres" subtitle="Explora los talleres disponibles" />
        <section className="gray-simple">
          <div className="container py-5">
            <ErrorState
              title="Error al cargar talleres"
              message={error}
              onRetry={() => fetchAllTalleres(false)}
            />
          </div>
        </section>
        <FooterTop bg="theme-bg" />
        <Footer />
      </>
    );
  }

  return (
    <>
      {/* Navegación consciente del usuario */}
      {user ? (
        <UserNav
          nombreUsuario={user.nombre}
          nombreInmobiliaria={user.inmobiliaria_nombre}
        />
      ) : (
        <Navbar transparent={false} />
      )}

      <PageTitle title="Listado de Talleres" subtitle="Explora los talleres disponibles" />

      <section className="gray-simple">
        <div className="container">
          {/* Botón para mostrar/ocultar filtros en móvil */}
          <div className="d-md-none mb-3">
            <button
              className="btn btn-outline-primary w-100"
              onClick={() => setMostrarFiltros(!mostrarFiltros)}
              aria-expanded={mostrarFiltros}
              aria-controls="filtros-talleres"
            >
              <i className={`fa-solid ${mostrarFiltros ? 'fa-chevron-up' : 'fa-filter'} me-2`} aria-hidden="true"></i>
              {mostrarFiltros ? 'Ocultar Filtros' : 'Mostrar Filtros'}
              {hayFiltrosActivos && (
                <span className="badge bg-primary ms-2">Activos</span>
              )}
            </button>
          </div>

          {/* Panel de Filtros */}
          <div
            id="filtros-talleres"
            className={`card mb-4 p-3 shadow-sm ${mostrarFiltros ? '' : 'd-none d-md-block'}`}
          >
            <h5 className="mb-3">
              <i className="fa-solid fa-filter me-2" aria-hidden="true"></i>
              Filtros de Búsqueda
            </h5>
            <div className="row g-3">
              {/* Filtro por nombre */}
              <div className="col-md-6 col-lg-4">
                <label htmlFor="filtro-nombre" className="form-label small fw-bold">
                  Nombre del Taller
                </label>
                <input
                  id="filtro-nombre"
                  type="text"
                  className="form-control"
                  placeholder="Buscar por nombre..."
                  value={filtros.nombre || ''}
                  onChange={(e) => actualizarFiltro('nombre', e.target.value)}
                />
              </div>

              {/* Filtro por mes */}
              <div className="col-md-6 col-lg-2">
                <label htmlFor="filtro-mes" className="form-label small fw-bold">
                  Mes
                </label>
                <input
                  id="filtro-mes"
                  type="month"
                  className="form-control"
                  value={filtros.mes || ''}
                  onChange={(e) => actualizarFiltro('mes', e.target.value)}
                />
              </div>

              {/* Filtro por costo */}
              <div className="col-md-4 col-lg-2">
                <label htmlFor="filtro-costo" className="form-label small fw-bold">
                  Tipo de Costo
                </label>
                <select
                  id="filtro-costo"
                  className="form-select"
                  value={filtros.costo || 'todos'}
                  onChange={(e) => actualizarFiltro('costo', e.target.value)}
                >
                  <option value="todos">Todos</option>
                  <option value="gratuito">Gratuito</option>
                  <option value="pago">De pago</option>
                </select>
              </div>

              {/* Filtro por créditos */}
              <div className="col-md-4 col-lg-2">
                <label htmlFor="filtro-creditos" className="form-label small fw-bold">
                  Créditos
                </label>
                <select
                  id="filtro-creditos"
                  className="form-select"
                  value={filtros.creditos || 'todos'}
                  onChange={(e) => actualizarFiltro('creditos', e.target.value)}
                >
                  <option value="todos">Todos</option>
                  <option value="con_creditos">Con Créditos</option>
                  <option value="sin_creditos">Sin Créditos</option>
                </select>
              </div>

              {/* Filtro por estado */}
              <div className="col-md-4 col-lg-2">
                <label htmlFor="filtro-estado" className="form-label small fw-bold">
                  Estado
                </label>
                <select
                  id="filtro-estado"
                  className="form-select"
                  value={filtros.estado || 'todos'}
                  onChange={(e) => actualizarFiltro('estado', e.target.value)}
                >
                  <option value="todos">Todos</option>
                  <option value="proximo">Próximo</option>
                  <option value="en_curso">En Curso</option>
                  <option value="finalizado">Finalizado</option>
                </select>
              </div>
            </div>

            {/* Resumen y botón limpiar */}
            <div className="mt-3 d-flex justify-content-between align-items-center flex-wrap">
              <span className="text-muted small">
                <i className="fa-solid fa-list me-1" aria-hidden="true"></i>
                Mostrando {talleresFiltrados.length} de {talleres.length} talleres
                {activos.length > 0 && ` (${activos.length} activos)`}
              </span>
              {hayFiltrosActivos && (
                <button
                  className="btn btn-outline-secondary btn-sm mt-2 mt-md-0"
                  onClick={limpiarFiltros}
                >
                  <i className="fa-solid fa-times me-1" aria-hidden="true"></i>
                  Limpiar Filtros
                </button>
              )}
            </div>
          </div>

          {/* Contenido principal */}
          {talleres.length === 0 ? (
            <EmptyState
              icon="fa-solid fa-chalkboard-user"
              title="No hay talleres disponibles"
              message="Actualmente no hay talleres programados. Vuelve pronto para ver nuevos eventos."
            />
          ) : talleresFiltrados.length === 0 ? (
            <div className="alert alert-info">
              <i className="fa-solid fa-info-circle me-2" aria-hidden="true"></i>
              No se encontraron talleres con los filtros aplicados.
              <button
                className="btn btn-link p-0 ms-2"
                onClick={limpiarFiltros}
              >
                Limpiar filtros
              </button>
            </div>
          ) : (
            <>
              {/* Talleres Activos (Próximos + En Curso) */}
              {activos.length > 0 && (
                <>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h4 className="mb-0">
                      <i className="fa-solid fa-calendar-check me-2 text-success" aria-hidden="true"></i>
                      Talleres Disponibles
                      <span className="badge bg-success ms-2">{activos.length}</span>
                    </h4>
                  </div>
                  <div className="row">
                    {activos.map(t => renderTaller(t))}
                  </div>
                </>
              )}

              {activos.length === 0 && finalizados.length > 0 && (
                <div className="alert alert-info mb-4">
                  <i className="fa-solid fa-info-circle me-2" aria-hidden="true"></i>
                  No hay talleres activos en este momento. Consulta el historial de talleres finalizados.
                </div>
              )}

              {/* Talleres Finalizados - Sección colapsable */}
              {finalizados.length > 0 && (
                <div className="mt-4">
                  <button
                    className="btn btn-outline-secondary w-100 d-flex justify-content-between align-items-center py-3"
                    onClick={() => setMostrarFinalizados(!mostrarFinalizados)}
                    aria-expanded={mostrarFinalizados}
                    aria-controls="seccion-finalizados"
                  >
                    <span>
                      <i className="fa-solid fa-clock-rotate-left me-2" aria-hidden="true"></i>
                      Talleres Finalizados
                      <span className="badge bg-secondary ms-2">{finalizados.length}</span>
                    </span>
                    <i className={`fa-solid fa-chevron-${mostrarFinalizados ? 'up' : 'down'}`} aria-hidden="true"></i>
                  </button>

                  {mostrarFinalizados && (
                    <div id="seccion-finalizados" className="row mt-3">
                      {finalizados.map(t => renderTaller(t, true))}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </section>

      <FooterTop bg="theme-bg" />
      <Footer />
    </>
  );
}
